import React, { useEffect, useState } from 'react';
import UnitSelect from './UnitSelect';
import QuantityConversionHint from './QuantityConversionHint';
import { getUnitFormulaForProduct } from '../../utils/unitSelectUtils';

/**
 * Vista previa de importación Excel (órdenes de compra, documentos y requerimientos).
 */
const ComprasItemsImportPreviewModal = ({
  isOpen,
  onClose,
  onConfirm,
  fileName = '',
  items = [],
  errors = [],
  warnings = [],
  projects = [],
  products = [],
  units = [],
  confirmLabel = 'Añadir filas',
  entityLabel = 'el formulario',
  showTipo = false,
  hidePricing = false,
  showNotes = false,
}) => {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setRows(items.map((item) => ({ ...item })));
    }
  }, [isOpen, items]);

  if (!isOpen) return null;

  const nOk = rows.length;
  const nErr = errors.length;
  const nWarn = warnings.length;
  const canEditUnits = products.length > 0 && units.length > 0;

  const projectLabel = (id) => {
    if (id == null || id === '') return '—';
    const p = projects.find((x) => String(x.id) === String(id));
    if (p) {
      return [p.code, p.name].filter(Boolean).join(' — ') || String(id);
    }
    return `ID ${id}`;
  };

  const findProduct = (row) => {
    if (!row.product_id) return null;
    return products.find((p) => String(p.id) === String(row.product_id)) || null;
  };

  const handleUnitChange = (index, unitCode) => {
    setRows((prev) => {
      const next = [...prev];
      const product = findProduct(next[index]);
      next[index] = {
        ...next[index],
        unit_of_measure: unitCode,
        _unitFormula: product ? getUnitFormulaForProduct(product, unitCode) : '',
        _unitIsCustom: Boolean(
          product?.conversions?.some(
            (c) =>
              c.is_active !== false &&
              c.from_unit?.code &&
              String(c.from_unit.code).toLowerCase() === String(unitCode).toLowerCase(),
          ),
        ),
      };
      return next;
    });
  };

  const canAdd = nOk > 0;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="compras-import-preview-title"
    >
      <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        <div className="p-4 sm:p-5 border-b border-gray-200 flex justify-between items-start gap-3">
          <div>
            <h2 id="compras-import-preview-title" className="text-lg font-semibold text-gray-900">
              Validación de importación
            </h2>
            {fileName ? (
              <p className="text-sm text-gray-600 mt-1">
                Archivo: <span className="font-mono text-gray-800">{fileName}</span>
              </p>
            ) : null}
            <p className="text-sm text-gray-600 mt-2">
              Revise el resumen. Solo se añadirán las filas válidas. Puede ajustar la unidad antes de confirmar.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-1 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            aria-label="Cerrar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 font-medium text-emerald-800 ring-1 ring-inset ring-emerald-200">
              Filas listas: {nOk}
            </span>
            {nErr > 0 && (
              <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 font-medium text-red-800 ring-1 ring-inset ring-red-200">
                Errores: {nErr}
              </span>
            )}
            {nWarn > 0 && (
              <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 font-medium text-amber-900 ring-1 ring-inset ring-amber-200">
                Advertencias: {nWarn}
              </span>
            )}
          </div>

          {nErr > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50/80 p-3 text-sm text-red-900">
              <p className="font-medium mb-1">No se pudo leer o rechazó (por fila):</p>
              <ul className="list-disc pl-5 space-y-0.5 max-h-32 overflow-y-auto">
                {errors.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          )}

          {nWarn > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-3 text-sm text-amber-950">
              <p className="font-medium mb-1">Atención:</p>
              <ul className="list-disc pl-5 space-y-0.5 max-h-28 overflow-y-auto">
                {warnings.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          )}

          {nOk > 0 ? (
            <div>
              <p className="text-sm font-medium text-gray-800 mb-2">Vista previa de filas a añadir</p>
              <div className="border border-gray-200 rounded-lg overflow-x-auto max-h-64 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs font-medium text-gray-600 uppercase">
                    <tr>
                      <th className="px-2 py-2 whitespace-nowrap">#</th>
                      {showTipo && <th className="px-2 py-2 whitespace-nowrap">Tipo</th>}
                      <th className="px-2 py-2 min-w-[10rem]">Ítem</th>
                      <th className="px-2 py-2 text-right">Cantidad</th>
                      <th className="px-2 py-2 min-w-[9rem]">Unidad</th>
                      <th className="px-2 py-2 min-w-[8rem]">Proyecto</th>
                      {showNotes && <th className="px-2 py-2 min-w-[8rem]">Obs.</th>}
                      {!hidePricing && (
                        <>
                          <th className="px-2 py-2 text-right whitespace-nowrap">P. unit.</th>
                          <th className="px-2 py-2 text-right">Total</th>
                          <th className="px-2 py-2 whitespace-nowrap">IGV</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((row, i) => {
                      const product = findProduct(row);
                      const isProductRow = row.item_type !== 'service' && row.product_id;
                      return (
                        <tr key={i} className="hover:bg-gray-50/80">
                          <td className="px-2 py-1.5 text-gray-500">{i + 1}</td>
                          {showTipo && (
                            <td className="px-2 py-1.5 whitespace-nowrap text-xs">
                              {row._tipoLabel || (row.item_type === 'service' ? 'Servicio' : 'Producto')}
                            </td>
                          )}
                          <td className="px-2 py-1.5">
                            <div className="font-medium text-gray-900 break-words">
                              {row._label || row.description || '—'}
                            </div>
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <div className="font-mono">{row.quantity != null ? row.quantity : '—'}</div>
                            {isProductRow && (
                              <QuantityConversionHint
                                product={product}
                                unitCode={row.unit_of_measure}
                                quantity={row.quantity}
                              />
                            )}
                          </td>
                          <td className="px-2 py-1.5 min-w-[9rem]">
                            {canEditUnits && isProductRow ? (
                              <UnitSelect
                                value={row.unit_of_measure || ''}
                                onChange={(v) => handleUnitChange(i, v)}
                                units={units}
                                product={product}
                                compact
                              />
                            ) : (
                              <div>
                                <span className="whitespace-nowrap">{row.unit_of_measure || '—'}</span>
                                {row._unitFormula ? (
                                  <p className="text-[10px] text-teal-700 mt-0.5">{row._unitFormula}</p>
                                ) : null}
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-xs text-gray-700 break-words">
                            {projectLabel(row.project_id)}
                          </td>
                          {showNotes && (
                            <td className="px-2 py-1.5 text-xs text-gray-600 break-words">
                              {row.notes || '—'}
                            </td>
                          )}
                          {!hidePricing && (
                            <>
                              <td className="px-2 py-1.5 text-right font-mono">
                                {row.unit_price != null ? row.unit_price : '0'}
                              </td>
                              <td className="px-2 py-1.5 text-right font-mono">
                                {row.total != null ? row.total : '0'}
                              </td>
                              <td className="px-2 py-1.5 whitespace-nowrap text-xs">
                                {row.has_igv === false ? 'No' : 'Sí'}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-600">No hay filas válidas para añadir. Corrija el archivo e intente otra vez.</p>
          )}
        </div>

        <div className="p-4 sm:p-5 border-t border-gray-200 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onConfirm(rows)}
            disabled={!canAdd}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {canAdd ? `${confirmLabel} (${nOk})` : 'No hay filas para añadir'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComprasItemsImportPreviewModal;
